import { Navigate } from "react-router-dom";
import { RouteType } from "../@types/RouteType";
import { RoleType } from "../@types/Auth";

// Icons
import { ContactSupport, FormatListBulletedOutlined } from "@mui/icons-material";
import HistoryIcon from '@mui/icons-material/History';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import DashboardSharpIcon from '@mui/icons-material/DashboardSharp';
import LogoutIcon from '@mui/icons-material/Logout';
import VerifiedIcon from '@mui/icons-material/Verified';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import DescriptionIcon from '@mui/icons-material/Description';

// Lazy Loaded
import DashboardPage from "../pages/DashboardPage/DashboardPage.lazy";
import OrdersPage from "../pages/OrdersPage/OrdersPage.lazy";
import OrderHistoryXSeriesCarts from "../components/OrdersPage/OrderHistoryCarts/OrderHistoryCarts.lazy";
import PreviousOrderXSeriesCart from "../components/OrdersPage/PreviousOrderCart/PreviousOrderCart.lazy";
import OrderHistoryDisplays from "../components/OrdersPage/OrderHistoryDisplays/OrderHistoryDisplays.lazy";
import DealerDisplayOrderForm from "../components/DealerDisplayOrderForm/DealerDisplayOrderForm.lazy";
import PreviousOrderDealerDisplays from "../components/OrdersPage/PreviousOrderDealerDisplays/PreviousOrderDealerDisplays.lazy";
import OrderHistoryPrintedMaterials from "../components/OrdersPage/OrderHistoryPrintedMaterials/OrderHistoryPrintedMaterials.lazy";
import PrintMaterialOrderForm from "../components/OrdersPage/PrintMaterialOrderForm/PrintMaterialOrderForm.lazy";
import PreviousOrderPrintedMaterials from "../components/OrdersPage/PreviousOrderPrintedMaterials/PreviousOrderPrintedMaterials.lazy";
import WarrantyRegistrationPage from "../pages/WarrantyRegistrationPage/WarrantyRegistrationPage.lazy";
import WarrantyClaimsPage from "../pages/WarrantyClaimsPage/WarrantyClaimsPage.lazy";
import WarrantyClaimDetail from "../components/WarrantyClaimsPage/WarrantyClaimDetail/WarrantyClaimDetail.lazy";
import WarrantyClaimForm from "../components/WarrantyClaimsPage/WarrantyClaimForm/WarrantyClaimForm.lazy";
import TechSupportPage from "../pages/TechSupportPage/TechSupportPage.lazy";
import TechSupportDetail from "../components/TechSupportPage/TechSupportDetail/TechSupportDetail.lazy";
import TechSupportForm from "../components/TechSupportPage/TechSupportForm/TechSupportForm.lazy";
import DealerResourcesPage from "../pages/DealerResourcesPage/DealerResourcesPage.lazy";
import DealerProfilePage from "../pages/DealerProfilePage/DealerProfilePage.lazy";
import InvoicesPage from "../pages/InvoicesPage/InvoicesPage.lazy";
import InvoiceDetail from "../components/InvoicePage/InvoiceDetail/InvoiceDetail.lazy";
import McoLsvOrderForm from "../components/OrdersPage/McoLsvOrderForm/McoLsvOrderForm.lazy";
import OrderHistoryMco from "../components/OrdersPage/OrderHistoryMco/OrderHistoryMco.lazy";
import PreviousOrderMco from "../components/OrdersPage/PreviousOrderMco/PreviousOrderMco.lazy";
import CartOrderPage from "../pages/CartOrderPage/CartOrderPage.lazy";
import LeadsPage from "../pages/LeadsPage/LeadsPage.lazy";
import LeadDetail from "../components/LeadDetail/LeadDetail.lazy";
import AdminPage from "../pages/AdminPage/AdminPage.lazy";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AdminDealer from "../components/AdminPage/AdminDealer/AdminDealer";
import AdminGlobalSettings from "../components/AdminPage/AdminGlobalSettings/AdminGlobalSettings";
import AdminInventory from "../components/AdminPage/AdminInventory/AdminInventory.lazy";
import AdminBulletinsTable from "../components/AdminPage/AdminGlobalSettings/AdminBulletinsTable/AdminBulletinsTable.lazy";
import AdminWarrantyRegistrationSearch from "../components/AdminPage/AdminGlobalSettings/AdminWarrantyRegistrationSearch/AdminWarrantyRegistrationSearch.lazy";
import AdminLeads from "../components/AdminLeads/AdminLeads.lazy";
import AdminDealersPage from "../pages/AdminDealersPage/AdminDealersPage.lazy";
import InStoreSignageOrderForm from "../components/OrdersPage/InStoreSignageOrderForm/InStoreSignageOrderForm.lazy";
import VehicleHistoryPage from "../pages/VehicleHistoryPage/VehicleHistoryPage.lazy";
import PreviousOrderMerchandise from "../components/OrdersPage/PreviousOrderMerchandise/PreviousOrderMerchandise.lazy";
import OrderHistoryMerchandise from "../components/OrdersPage/OrderHistoryPop/OrderHistoryPop.lazy";

// Tour Steps
import {
    DealerProfile,
    TechSupportHistory,
    TourStepsDashboard,
    TourStepsOrdersPrintMedia,
    TourStepsOrderHistory,
    TourStepsCartOrderPage,
    TourStepsCartOrderPageWInventory,
    WarrantyClaimHistory,
    TourStepsLeads,
    TourStepsLeadsDetail
} from "../components/TourSteps";
import VehicleHistoryDetailPage from "../components/VehicleHistoryDetailPage/VehicleHistoryDetailPage";

import { createSvgIcon } from '@mui/material/utils';
const GolfCartIcon = createSvgIcon(
    <svg fill="currentColor" width="800px" height="800px" viewBox="0 0 50 50"><path d="M23 7C14.523438 7 8.8125 8 8.8125 8C8.261719 8.101563 7.894531 8.636719 8 9.1875C8.101563 9.738281 8.636719 10.105469 9.1875 10C9.1875 10 9.945313 9.890625 11 9.75L11 18L6 18L6 20L11 20L11 24.03125C13.097656 24.128906 15.246094 24.363281 17.34375 24.75C16.949219 23.519531 16.421875 21.949219 15.9375 20.65625C15.296875 18.960938 13.949219 18.34375 13 18.125L13 9.5C15.410156 9.25 18.855469 9 23 9C26.136719 9 28.871094 9.136719 31.0625 9.3125C31.070313 9.34375 31.082031 9.375 31.09375 9.40625L37.90625 25L34.34375 33L26 33L26 31.28125C26 30.152344 25.277344 29.167969 24.125 28.71875C19.441406 26.882813 13.253906 25.835938 7.96875 26C6.21875 26.054688 5.015625 26.449219 4.28125 27.21875C2.273438 29.320313 2 32.839844 2 36C2 36.550781 2.449219 37 3 37L4.09375 37C4.027344 37.324219 4 37.65625 4 38C4 40.757813 6.242188 43 9 43C11.414063 43 13.441406 41.277344 13.90625 39L38.09375 39C38.558594 41.277344 40.585938 43 43 43C45.757813 43 48 40.757813 48 38C48 37.773438 47.964844 37.5625 47.9375 37.34375C47.972656 37.238281 48 37.117188 48 37L48 31C48 30.917969 47.988281 30.828125 47.96875 30.75C47.105469 27.292969 41.171875 24.78125 39.75 24.21875L33.3125 9.53125C35.503906 9.769531 36.8125 10 36.8125 10C37.363281 10.105469 37.898438 9.738281 38 9.1875C38.105469 8.636719 37.738281 8.101563 37.1875 8C37.1875 8 31.476563 7 23 7 Z M 30.875 16C30.652344 16.023438 30.441406 16.125 30.28125 16.28125L26.28125 20.28125C25.882813 20.679688 25.882813 21.320313 26.28125 21.71875C26.679688 22.117188 27.320313 22.117188 27.71875 21.71875L29 20.4375L35.0625 26.46875L35.9375 24.5L30.4375 19L31.71875 17.71875C32.042969 17.417969 32.128906 16.941406 31.933594 16.546875C31.742188 16.148438 31.308594 15.929688 30.875 16 Z M 9 35C10.652344 35 12 36.347656 12 38C12 39.652344 10.652344 41 9 41C7.347656 41 6 39.652344 6 38C6 36.347656 7.347656 35 9 35 Z M 43 35C44.652344 35 46 36.347656 46 38C46 39.652344 44.652344 41 43 41C41.347656 41 40 39.652344 40 38C40 36.347656 41.347656 35 43 35Z"/></svg>,
    'GolfCartIcon'
)

export const DashboardRoutes: RouteType[] = [
    {
        index: true,
        element: <Navigate to="/dashboard" />,
        state: "dashboard",
        displayText: 'Dashboard',
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerService,
            RoleType.DealerSupport
        ],
    },
    {
        path: "/dashboard",
        element: <DashboardPage />,
        state: "dashboard",
        displayText: 'Dashboard',
        tutorialType: 'Dashboard',
        hasTutorial: true,
        tutorialSteps: TourStepsDashboard,
        sidebarProps: {
            displayText: "Dashboard",
            icon: <DashboardSharpIcon />,
            id: "DashboardNavLink"
        },
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerService,
            RoleType.DealerSupport
        ],
    },
]

export const OrderRoutes: RouteType[] = [
    {
        path: "/orders",
        element: <OrdersPage />,
        state: "orders",
        displayText: 'Orders',
        sidebarProps: {
            displayText: "Orders",
            icon: <HistoryIcon />,
            id: "OrdersNavLink"
        },
        children: [
            {
                index: true,
                element: <Navigate to="./carts" />,
                state: "orders.carts",
                displayText: 'Carts',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/carts",
                element: <OrderHistoryXSeriesCarts />,
                state: "orders.carts",
                displayText: 'Carts',
                hasTutorial: true,
                tutorialType: 'Carts',
                tutorialSteps: TourStepsOrderHistory,
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/carts/new-order",
                element: <CartOrderPage />,
                state: "orders.carts.new-order",
                displayText: 'New Cart Order',
                hasTutorial: true,
                tutorialType: 'Cart Order',
                tutorialSteps: TourStepsCartOrderPage,
                sidebarProps: {
                    displayText: "New Cart Order"
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/carts/:id",
                element: <PreviousOrderXSeriesCart />,
                state: "orders.carts.id",
                displayText: 'Order',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/displays",
                element: <OrderHistoryDisplays />,
                state: "orders.displays",
                displayText: 'Show Room Displays',
                hasTutorial: true,
                tutorialType: 'Displays',
                tutorialSteps: TourStepsOrderHistory,
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            /*
            {
                path: "/orders/displays/new-order",
                element: <DealerDisplayOrderForm />,
                state: "orders.displays.new-order",
                displayText: "New Showroom Display Order",
                sidebarProps: {
                    displayText: "New Display Order"
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            */
            {
                path: "/orders/displays/:id",
                element: <PreviousOrderDealerDisplays />,
                state: "orders.displays.id",
                displayText: 'Order',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/print-materials",
                element: <OrderHistoryPrintedMaterials />,
                state: "orders.print-materials",
                displayText: 'Print Materials',
                hasTutorial: true,
                tutorialType: 'Print Materials',
                tutorialSteps: TourStepsOrdersPrintMedia,
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/print-materials/new-order",
                element: <PrintMaterialOrderForm />,
                state: "orders.print-materials.new-order",
                displayText: 'New Print Materials Order',
                sidebarProps: {
                    displayText: "New Print Materials Order",
                    id: "NewPrintedMaterialsOrder"
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/print-materials/:id",
                element: <PreviousOrderPrintedMaterials />,
                state: "orders.print-materials.id",
                displayText: 'Order',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/mco-lsv",
                element: <OrderHistoryMco />,
                state: "orders.mco-lsv",
                displayText: 'MCO | LSV',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/mco-lsv/new-order",
                element: <McoLsvOrderForm />,
                state: "orders.mco-lsv.new-order",
                displayText: 'New MCO & LSV Kit Order',
                sidebarProps: {
                    displayText: "New MCO | LSV Order",
                    id: "NewMcoLSVOrder"
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/mco-lsv/:id",
                element: <PreviousOrderMco />,
                state: "orders.mco-lsv.id",
                displayText: 'Order',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/in-store-signage/:id",
                element: <PreviousOrderMerchandise />,
                state: "orders.in-store-signage.id",
                displayText: 'In-Store Signage Order',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/in-store-signage",
                element: <OrderHistoryMerchandise />,
                state: "orders.in-store-signage",
                displayText: 'In-Store Signage',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/orders/in-store-signage/new-order",
                element: <InStoreSignageOrderForm />,
                state: "orders.in-store-signage.new-order",
                displayText: 'New In-Store Signage Order',
                sidebarProps: {
                    displayText: "New In-Store Signage Order",
                    id: "NewPopOrder"
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            }
        ],
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerSupport
        ],
    }
]

export const RegistrationRoutes: RouteType[] = [
    {
        path: "/registration",
        element: <WarrantyRegistrationPage />,
        state: "registration",
        displayText: 'Warranty Registration',
        sidebarProps: {
            displayText: "Warranty Registration",
            icon: <AppRegistrationIcon />,
            id: "WarrantyRegistrationNavLink"
        },
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerSupport
        ],
    }
]

export const WarrantyClaimRoutes: RouteType[] = [
    {
        path: "/warranty-claims",
        element: <WarrantyClaimsPage />,
        state: "warranty-claims",
        displayText: 'Warranty Claims',
        tutorialType: 'Warranty Claims',
        hasTutorial: true,
        tutorialSteps: WarrantyClaimHistory,
        sidebarProps: {
            displayText: "Warranty Claims",
            icon: <VerifiedIcon />,
            id: "WarrantyClaimsNavLink"
        },
        children: [
            {
                path: "/warranty-claims",
                element: <Navigate to=".." />,
                state: "warranty-claims.view-all",
                displayText: 'Warranty Claims',
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerService,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/warranty-claims/:id",
                element: <WarrantyClaimDetail />,
                state: "warranty-claims.id",
                displayText: 'Warranty Claim',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerService,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/warranty-claims/new-ticket",
                element: <WarrantyClaimForm />,
                state: "warranty-claims.new-ticket",
                displayText: "New Warranty Claim",
                sidebarProps: {
                    displayText: "New Claim",
                    id: "NewWarrantyClaim"
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerService,
                    RoleType.DealerSupport
                ],
            }
        ],
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerService,
            RoleType.DealerSupport
        ],
    }
]

export const TechSupportRoutes: RouteType[] = [
    {
        path: "/technical-support",
        element: <TechSupportPage />,
        state: "technical-support",
        displayText: 'Technical Support',
        tutorialType: 'Tech Support',
        hasTutorial: true,
        tutorialSteps: TechSupportHistory,
        sidebarProps: {
            displayText: "Technical Support",
            icon: <ContactSupport />,
            id: "TechSupportNavLink"
        },
        children: [
            {
                path: "technical-support",
                element: <Navigate to=".." />,
                state: "technical-support.view-all",
                displayText: 'Technical Support',
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerService,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/technical-support/:id",
                element: <TechSupportDetail />,
                state: "technical-support.id",
                displayText: 'Technical Support',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerService,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/technical-support/new-ticket",
                element: <TechSupportForm />,
                state: "technical-support.new-ticket",
                displayText: "New Technical Support Ticket",
                sidebarProps: {
                    displayText: "New Support Ticket"
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerService,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "https://brandfolder.com/madjax-xseries/madjax-xseries-dealer-resources?o=Strict&q=Technical+Information",
                externalPath: true,
                element: <></>,
                state: "technical-support-docs",
                displayText: "Technical Documents",
                sidebarProps: {
                    displayText: "Technical Documents"
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerService,
                    RoleType.DealerSupport
                ],
            },
        ],
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerService,
            RoleType.DealerSupport
        ],
    },
]

export const FinancialRoutes: RouteType[] = [
    {
        path: '/invoices',
        element: <InvoicesPage />,
        state: 'invoices',
        displayText: 'Invoices',
        sidebarProps: {
            displayText: 'Invoices',
            icon: <DescriptionIcon />,
            id: 'InvoicesNavLink'
        },
        children: [
            {
                index: true,
                path: "invoices",
                element: <Navigate to=".." />,
                state: "invoices",
                displayText: 'Invoices',
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/invoices/:id",
                element: <InvoiceDetail />,
                state: "invoices.id",
                displayText: 'Invoice',
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSupport
                ],
            },
        ],
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSupport
        ],
    },
    {
        path: "https://onesource.dllgroup.com/",
        externalPath: true,
        element: <></>,
        state: "dealer.financing",
        displayText: 'Dealer Financing',
        sidebarProps: {
            displayText: "Dealer Financing",
            icon: <AccountBalanceIcon />,
            id: "DDLNavLink"
        },
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSupport
        ],
    }
]

export const LeadsRoutes: RouteType[] = [
    {
        path: "/leads",
        element: <LeadsPage />,
        state: "leads",
        displayText: 'Leads',
        sidebarProps: {
            displayText: "Leads",
            icon: <ConnectWithoutContactIcon />,
            id: "LeadsNavLink"
        },
        hasTutorial: true,
        tutorialSteps: TourStepsLeads,
        children: [
            {
                path: "/leads",
                element: <Navigate to=".." />,
                state: "leads.view-all",
                displayText: "Leads",
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                },
                hasTutorial: true,
                tutorialSteps: TourStepsLeads,
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/leads/:id",
                element: <LeadDetail />,
                state: "leads.id",
                displayText: "Lead",
                hasTutorial: true,
                tutorialSteps: TourStepsLeadsDetail,
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerSupport
                ],
            }
        ],
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerSupport
        ],
    }
]

export const ResourcesRoutes: RouteType[] = [
    {
        path: "/resources",
        element: <DealerResourcesPage />,
        state: "resources",
        displayText: 'Marketing Resources',
        sidebarProps: {
            displayText: "Marketing Resources",
            icon: <FormatListBulletedOutlined />,
            id: "ResourcesNavLink"
        },
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerService,
            RoleType.DealerSupport
        ],
    }
]

export const VehicleHistoryRoutes: RouteType[] = [
    {
        path: '/vehicle-history',
        element: <VehicleHistoryPage />,
        state: 'vehicle-history',
        displayText: 'Vehicle History',
        sidebarProps: {
            displayText: "Vehicle History",
            icon: <GolfCartIcon />,
            id: 'VehicleHistoryNavLink'
        },
        children: [
            {
                path: "vehicle-history",
                element: <Navigate to=".." />,
                state: "vehicle-history.view-all",
                displayText: 'Vehicle History',
                sidebarProps: {
                    displayText: "View All",
                    mobileOnly: true
                },
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerService,
                    RoleType.DealerSupport
                ],
            },
            {
                path: "/vehicle-history/:id",
                element: <VehicleHistoryDetailPage />,
                state: "vehicle-history.id",
                displayText: "Vehicle History Detail",
                authorizedRoles: [
                    RoleType.Admin,
                    RoleType.DealerAdmin,
                    RoleType.DealerSales,
                    RoleType.DealerService,
                    RoleType.DealerSupport
                ]
            }
        ],
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerService,
            RoleType.DealerSupport
        ]
    }
]

export const AdminUserRoutes: RouteType[] = [
    {
        path: "/admin",
        element: <AdminPage />,
        state: "admin",
        displayText: 'Admin',
        sidebarProps: {
            displayText: "Admin",
            icon: <AdminPanelSettingsIcon />,
            id: "AdminNavLink"
        },
        children: [
            {
                index: true,
                element: <Navigate to="/admin/dealers" />,
                state: "admin.dealers",
                displayText: 'Dealers',
                authorizedRoles: [RoleType.Admin],
            },
            {
                path: "/admin/dealers",
                element: <AdminDealersPage />,
                state: "admin.dealers",
                displayText: "Dealers",
                sidebarProps: {
                    displayText: 'Dealers'
                },
                authorizedRoles: [RoleType.Admin],
            },
            {
                path: "/admin/dealers/:id",
                element: <AdminDealer />,
                state: "admin.dealers.id",
                displayText: "Company",
                authorizedRoles: [RoleType.Admin],
            },
            {
                path: '/admin/dealers/:id/:id',
                element: <LeadDetail />,
                state: 'admin.dealers.id.leads.id',
                displayText: '',
                authorizedRoles: [RoleType.Admin]
            },

            {
                path: "/admin/settings",
                element: <AdminGlobalSettings />,
                state: "admin.settings",
                displayText: "Settings",
                sidebarProps: {
                    displayText: 'Settings'
                },
                children: [
                    {
                        index: true,
                        element: <Navigate to="/admin/settings/bulletins" />,
                        state: 'admin.settings',
                        displayText: 'Bulletins',
                        authorizedRoles: [RoleType.Admin],
                    },
                    {
                        path: "/admin/settings/bulletins",
                        element: <AdminBulletinsTable />,
                        state: 'admin.settings.bulletins',
                        displayText: 'Bulletins',
                        authorizedRoles: [RoleType.Admin],
                    },
                    {
                        path: "/admin/settings/warranty-registration-search",
                        element: <AdminWarrantyRegistrationSearch />,
                        state: 'admin.settings.warranty-registration-search',
                        displayText: 'Warranty Registration Search',
                        authorizedRoles: [RoleType.Admin],
                    }
                ],
                authorizedRoles: [RoleType.Admin],
            },
            {
                path: "/admin/inventory",
                element: <AdminInventory />,
                state: "admin.inventory",
                displayText: "Inventory",
                sidebarProps: {
                    displayText: 'Inventory'
                },
                authorizedRoles: [RoleType.Admin],
            },
            {
                path: "/admin/leads",
                element: <AdminLeads />,
                state: "admin.leads",
                displayText: "Leads",
                sidebarProps: {
                    displayText: 'Leads'
                },
                authorizedRoles: [RoleType.Admin],
            },
            {
                path: '/admin/leads/:id',
                element: <LeadDetail />,
                state: 'admin.leads.id',
                displayText: "Lead",
                authorizedRoles: [RoleType.Admin],
            }
        ],
        authorizedRoles: [RoleType.Admin],
    }
]

export const UserRoutes: RouteType[] = [
    ...DashboardRoutes,
    ...OrderRoutes,
    ...RegistrationRoutes,
    ...WarrantyClaimRoutes,
    ...TechSupportRoutes,
    ...FinancialRoutes,
    ...LeadsRoutes,
    ...ResourcesRoutes,
    ...AdminUserRoutes,
    ...VehicleHistoryRoutes,
    {
        path: "/dealer-profile",
        element: <DealerProfilePage />,
        state: "dealer-profile",
        displayText: 'Dealer Profile',
        tutorialType: 'Dealer Profile',
        hasTutorial: true,
        tutorialSteps: DealerProfile,
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerService,
            RoleType.DealerSupport
        ],
    },
    {
        path: "/login",
        element: <></>,
        state: "logout",
        displayText: 'Logout',
        sidebarProps: {
            displayText: "Logout",
            icon: <LogoutIcon />,
            id: "LogoutNavLink"
        },
        authorizedRoles: [
            RoleType.Admin,
            RoleType.DealerAdmin,
            RoleType.DealerSales,
            RoleType.DealerService,
            RoleType.DealerSupport
        ],
    },
]


